<template>
  <b-modal
    id="create-user-modal"
    ref="createUserModal"
    title="Create Internal User"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hidden="resetForm"
  >
    <ValidationObserver v-slot="{ passes }">
      <form @submit.prevent="passes(submitForm)">
        <div class="row">
          <!-- First Name -->
          <div class="col-md-6">
            <ValidationProvider name="First Name" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="firstname">
                  First Name
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="firstname"
                  v-model="formData.firstname"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter first name"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Last Name -->
          <div class="col-md-6">
            <ValidationProvider name="Last Name" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="lastname">
                  Last Name
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="lastname"
                  v-model="formData.lastname"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter last name"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Email -->
          <div class="col-md-6">
            <ValidationProvider name="Email" rules="required|email" v-slot="{ errors }">
              <div class="form-group">
                <label for="email">
                  Email
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  v-model="formData.email"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter email address"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Phone Number -->
          <div class="col-md-6">
            <ValidationProvider name="Phone Number" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="phonenumber">
                  Phone Number
                  <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="phonenumber"
                  v-model="formData.phonenumber"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                  placeholder="Enter phone number"
                />
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Country -->
          <div class="col-md-6">
            <ValidationProvider name="Country" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="countryid">
                  Country
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="countryid"
                  v-model="formData.countryid"
                  :options="countryOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Country --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Role -->
          <div class="col-md-6">
            <ValidationProvider name="Role" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="roleid">
                  Role
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="roleid"
                  v-model="formData.roleid"
                  :options="roleOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Role --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Function -->
          <div class="col-md-6">
            <ValidationProvider name="Function" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="functionid">
                  Function
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="functionid"
                  v-model="formData.functionid"
                  :options="functionOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Function --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Service -->
          <div class="col-md-6">
            <ValidationProvider name="Service" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="serviceid">
                  Service
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="serviceid"
                  v-model="formData.serviceid"
                  :options="serviceOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Service --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>

          <!-- Access Type -->
          <div class="col-md-6">
            <ValidationProvider name="Access Type" rules="required" v-slot="{ errors }">
              <div class="form-group">
                <label for="accesstypeid">
                  Access Type
                  <span class="text-danger">*</span>
                </label>
                <b-form-select
                  id="accesstypeid"
                  v-model="formData.accesstypeid"
                  :options="accessTypeOptions"
                  class="form-control"
                  :class="{ 'is-invalid': errors[0] }"
                >
                  <template #first>
                    <b-form-select-option :value="null" disabled>
                      -- Select Access Type --
                    </b-form-select-option>
                  </template>
                </b-form-select>
                <span class="text-danger error-msg">{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
        </div>

        <!-- API Error Message -->
        <div v-if="apiError" class="alert alert-danger" role="alert">
          {{ apiError }}
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="outline-secondary"
            size="lg"
            class="mr-3"
            @click="hideModal"
            :disabled="isSubmitting"
          >
            Cancel
          </b-button>
          <b-button type="submit" variant="primary" size="lg" :disabled="isSubmitting">
            <span v-if="isSubmitting">
              <b-spinner small></b-spinner>
              Creating...
            </span>
            <span v-else>Create User</span>
          </b-button>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { InternalUserService } from "@/services/internalUserService";
import { mapGetters } from "vuex";

export default {
  name: "CreateUserModal",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      formData: {
        firstname: "",
        lastname: "",
        email: "",
        phonenumber: "",
        countryid: null,
        roleid: null,
        functionid: null,
        serviceid: null,
        accesstypeid: null,
      },
      isSubmitting: false,
      apiError: null,
      countryOptions: [],
      roleOptions: [],
      functionOptions: [],
      serviceOptions: [],
      accessTypeOptions: [],
    };
  },
  computed: {
    ...mapGetters(["countryList"]),
  },
  watch: {
    // Update country options when store data changes
    countryList: {
      immediate: true,
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.countryOptions = newValue.map((country) => ({
            value: country.countryid,
            text: country.countryname || country.name,
          }));
        }
      },
    },
  },
  mounted() {
    this.loadDropdownOptions();
  },
  methods: {
    show() {
      this.$refs.createUserModal.show();
    },

    hideModal() {
      this.$refs.createUserModal.hide();
    },

    resetForm() {
      this.formData = {
        firstname: "",
        lastname: "",
        email: "",
        phonenumber: "",
        countryid: null,
        roleid: null,
        functionid: null,
        serviceid: null,
        accesstypeid: null,
      };
      this.apiError = null;
      this.isSubmitting = false;
    },

    async loadDropdownOptions() {
      try {
        // Country list is already loaded globally in App.vue and available via countryList getter
        // The watcher will automatically populate countryOptions when data is available

        // Load all other dropdown options in parallel
        const [rolesRes, functionsRes, servicesRes, accessTypesRes] = await Promise.all([
          InternalUserService.getRoles(),
          InternalUserService.getFunctions(),
          InternalUserService.getServices(),
          InternalUserService.getAccessTypes(),
        ]);

        // Transform roles - API returns { details: [{id, name}] }
        if (rolesRes.data?.details && Array.isArray(rolesRes.data.details)) {
          this.roleOptions = rolesRes.data.details.map((role) => ({
            value: role.id,
            text: role.name,
          }));
        }

        // Transform functions - API returns { details: [{id, name}] }
        if (functionsRes.data?.details && Array.isArray(functionsRes.data.details)) {
          this.functionOptions = functionsRes.data.details.map((func) => ({
            value: func.id,
            text: func.name,
          }));
        }

        // Transform services - API returns { details: [{id, name}] }
        if (servicesRes.data?.details && Array.isArray(servicesRes.data.details)) {
          this.serviceOptions = servicesRes.data.details.map((service) => ({
            value: service.id,
            text: service.name,
          }));
        }

        // Transform access types - API returns { details: [{id, name}] }
        if (accessTypesRes.data?.details && Array.isArray(accessTypesRes.data.details)) {
          this.accessTypeOptions = accessTypesRes.data.details.map((type) => ({
            value: type.id,
            text: type.name,
          }));
        }
      } catch (error) {
        console.error("Error loading dropdown options:", error);
        this.$toast.error("Failed to load dropdown options. Please refresh the page.", {
          position: "top-right",
        });
      }
    },

    async submitForm() {
      this.isSubmitting = true;
      this.apiError = null;

      try {
        const response = await InternalUserService.createUser(this.formData);

        if (response.data && response.data.code === "0000") {
          this.$toast.success(response.data.message || "User created successfully!", {
            position: "top-right",
          });
          this.$emit("user-created");
          this.hideModal();
        } else {
          const errorMessage =
            response.data?.message || "Failed to create user. Please try again.";
          const errorDetail = response.data?.detail;
          this.apiError = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
          this.$toast.error(this.apiError, { position: "top-right" });
        }
      } catch (error) {
        console.error("Error creating user:", error);
        const errorMessage =
          error.response?.data?.message ||
          "An error occurred while creating the user. Please try again.";
        const errorDetail = error.response?.data?.detail;
        this.apiError = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
        this.$toast.error(this.apiError, { position: "top-right" });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.error-msg {
  font-size: 0.875rem;
  display: block;
  margin-top: 0.25rem;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.custom-control-label {
  font-weight: normal;
}
</style>
